// src/pages/admin/AdminCircumstancesScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, ToggleLeft, ToggleRight, MapPin, UserCheck, Clock, Flag } from 'lucide-react';

const DISTANCE_LABELS = [
  { distance: 0, label: 'Meets or exceeds', example: 'e.g. Position: Standard, Seeker: Immediately' },
  { distance: 1, label: '1 step short',     example: 'e.g. Position: ASAP, Seeker: Two Weeks' },
  { distance: 2, label: '2 steps short',    example: 'e.g. Position: ASAP, Seeker: One Month' },
  { distance: 3, label: '3 steps short',    example: 'e.g. Position: Urgent, Seeker: One Month' },
  { distance: 4, label: '4 steps short',    example: 'e.g. Position: Urgent, Seeker: Two Months' },
  { distance: 5, label: '5 steps short',    example: 'e.g. Position: Urgent, Seeker: Three Months' },
  { distance: 6, label: '6 steps short',    example: 'e.g. Position: Urgent, Seeker: Negotiable' },
];

const ORDINAL_MAP_DISPLAY = [
  { ordinal: 0, seeker: 'Immediately', position: 'Urgent' },
  { ordinal: 1, seeker: 'One Week',    position: 'ASAP' },
  { ordinal: 2, seeker: 'Two Weeks',   position: 'Priority' },
  { ordinal: 3, seeker: 'One Month',   position: 'Standard' },
  { ordinal: 4, seeker: 'Two Months',  position: '—' },
  { ordinal: 5, seeker: 'Three Months', position: '—' },
  { ordinal: 6, seeker: 'Negotiable',  position: 'Negotiable' },
];

const DEFAULTS = {
  workRights:  { hardFilter: true },
  availability: { hardFilter: true, distanceScores: [100, 90, 75, 55, 35, 15, 0] },
  residenceProximity: {
    cityMatchScore: 100,
  },
};

const FLAG_COLOURS = { RED: '#CC0000', ORANGE: '#E67E22', YELLOW: '#F1C40F' };

/* ── Score input (0-100) ────────────���────────────────────── */
const ScoreInput = ({ label, value, onChange, description }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number"
        min="0"
        max="100"
        value={value}
        onChange={e => {
          const v = Math.max(0, Math.min(100, parseInt(e.target.value) || 0));
          onChange(v);
        }}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888' }}>/ 100</span>
    </div>
  </div>
);

/* ── Flag indicator chip ────────────────────────────────── */
const FlagChip = ({ type, label }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
    background: FLAG_COLOURS[type] + '18', color: FLAG_COLOURS[type], border: `1px solid ${FLAG_COLOURS[type]}40`,
  }}>
    <Flag size={11} />
    {label}
  </span>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminCircumstancesScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    matchingModelAPI.getCircumstancesConfig()
      .then(({ data }) => {
        if (data.data) {
          // Merge with defaults to handle configs saved before flags migration
          setConfig({
            workRights:  { ...DEFAULTS.workRights, ...data.data.workRights },
            availability: { ...DEFAULTS.availability, ...data.data.availability },
            residenceProximity: { ...DEFAULTS.residenceProximity, ...data.data.residenceProximity },
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateCircumstancesConfig(config);
      setConfig({
        workRights:  { ...DEFAULTS.workRights, ...data.data.workRights },
        availability: { ...DEFAULTS.availability, ...data.data.availability },
        residenceProximity: { ...DEFAULTS.residenceProximity, ...data.data.residenceProximity },
      });
      toast.success('Circumstances scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateWR  = (patch) => setConfig(c => ({ ...c, workRights:  { ...c.workRights, ...patch } }));
  const updateAV  = (patch) => setConfig(c => ({ ...c, availability: { ...c.availability, ...patch } }));
  const updateRP  = (patch) => setConfig(c => ({ ...c, residenceProximity: { ...c.residenceProximity, ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const rp = config.residenceProximity;

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure how work rights, availability, and residence proximity affect candidate scoring.
        When hard filters are off, out-of-range values generate <strong>flags</strong> instead of excluding candidates.
      </p>

      {/* Flag legend */}
      <div style={{
        display: 'flex', gap: 16, padding: '12px 16px', marginBottom: 20,
        borderRadius: 8, border: '1px solid var(--border)', background: '#f8f8f8',
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#666', marginRight: 4 }}>Flag severity:</span>
        <FlagChip type="RED" label="Red — severe mismatch" />
        <FlagChip type="ORANGE" label="Orange — moderate mismatch" />
        <FlagChip type="YELLOW" label="Yellow — minor mismatch" />
      </div>

      {/* ── Work Rights ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <UserCheck size={18} />
          <span>Work Rights</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            When a seeker is a non-citizen and the position requires citizens only.
          </p>

          {/* Toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: 14, borderRadius: 8, border: '1px solid var(--border)', background: '#f8f8f8'
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Hard Filter</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {config.workRights.hardFilter
                  ? 'Non-citizens are excluded from the final match results.'
                  : <>Non-citizens are included in results. A <FlagChip type="RED" label="Red flag" /> is recorded on the comparison.</>}
              </div>
            </div>
            <button onClick={() => updateWR({ hardFilter: !config.workRights.hardFilter })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {config.workRights.hardFilter
                ? <ToggleRight size={36} color="#28A745" />
                : <ToggleLeft size={36} color="#999" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Availability ─��� */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Clock size={18} />
          <span>Availability</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            When a seeker marks themselves as unavailable (not looking for work).
          </p>

          {/* Hard filter toggle */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: 14, borderRadius: 8, border: '1px solid var(--border)', background: '#f8f8f8'
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Hard Filter</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                {config.availability.hardFilter
                  ? 'Unavailable seekers are excluded from the final match results.'
                  : <>Unavailable seekers are included in results. A <FlagChip type="RED" label="Red flag" /> is recorded on the comparison.</>}
              </div>
            </div>
            <button onClick={() => updateAV({ hardFilter: !config.availability.hardFilter })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {config.availability.hardFilter
                ? <ToggleRight size={36} color="#28A745" />
                : <ToggleLeft size={36} color="#999" />}
            </button>
          </div>

          {/* Ordinal distance scoring */}
          <div style={{ marginTop: 24 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Availability Distance Scoring</div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Score based on how far the seeker's availability falls short of the position's requirement.
            </p>

            {/* Ordinal mapping reference */}
            <div style={{
              padding: 16, borderRadius: 8, border: '1px solid var(--border)',
              background: '#f8f8f8', fontSize: 12, marginBottom: 20,
            }}>
              <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 11, color: '#3D80CC', letterSpacing: 1, marginBottom: 8 }}>
                Ordinal Scale
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #ddd' }}>
                    <th style={{ textAlign: 'center', padding: '4px 8px', color: '#999', fontWeight: 600 }}>#</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#999', fontWeight: 600 }}>Seeker (CV)</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: '#999', fontWeight: 600 }}>Position</th>
                  </tr>
                </thead>
                <tbody>
                  {ORDINAL_MAP_DISPLAY.map(r => (
                    <tr key={r.ordinal} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ textAlign: 'center', padding: '4px 8px', color: '#aaa' }}>{r.ordinal}</td>
                      <td style={{ padding: '4px 8px' }}>{r.seeker}</td>
                      <td style={{ padding: '4px 8px', color: r.position === '—' ? '#ccc' : undefined }}>{r.position}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Distance score inputs */}
            <div>
              {DISTANCE_LABELS.map(({ distance, label, example }) => (
                <div key={distance}>
                  {distance > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
                  <ScoreInput
                    label={`Distance ${distance}: ${label}`}
                    value={(config.availability.distanceScores || DEFAULTS.availability.distanceScores)[distance]}
                    onChange={(v) => {
                      const scores = [...(config.availability.distanceScores || DEFAULTS.availability.distanceScores)];
                      scores[distance] = v;
                      updateAV({ distanceScores: scores });
                    }}
                    description={example}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Residence Proximity ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <MapPin size={18} />
          <span>Residence Proximity</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Cascading location match: country &rarr; state &rarr; city.
            Non-matching levels generate flags on the comparison.
          </p>

          {/* Flag mapping */}
          <div style={{
            padding: 16, borderRadius: 8, border: '1px solid var(--border)',
            background: '#f8f8f8', fontSize: 12, marginBottom: 20,
          }}>
            <div style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 11, color: '#3D80CC', letterSpacing: 1, marginBottom: 10 }}>
              Mismatch Flags
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FlagChip type="RED" label="Red" />
                <span style={{ color: '#666' }}>Country does not match</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FlagChip type="ORANGE" label="Orange" />
                <span style={{ color: '#666' }}>Country matches, state does not</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FlagChip type="YELLOW" label="Yellow" />
                <span style={{ color: '#666' }}>Country and state match, city does not</span>
              </div>
            </div>
          </div>

          {/* City match score */}
          <ScoreInput
            label="City match score"
            value={rp.cityMatchScore}
            onChange={(v) => updateRP({ cityMatchScore: v })}
            description="Full match — country, state, and city all match"
          />
        </div>
      </div>

      {/* ─�� Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
